import type { ReactNode } from "react";

// Add a country here, in the payment translations and in TrustedShopsBadge.test.tsx; keep country checks out of TrustBadgeV3.

export type TrustProvider = "trustedshops" | "trustpilot";

export type TrustBadgeLocale = {
  code: string;
  provider: TrustProvider;
  flag: ReactNode;
  boxBorder: string;
  // Phones only: count and label go on two lines, since a wrapped line would leave the counter box full width.
  stackCount?: boolean;
};

const svgFlagClass = "absolute inset-0 w-full h-full";

export const TRUST_BADGE_LOCALES: Record<string, TrustBadgeLocale> = {
  DE: {
    code: "de",
    provider: "trustedshops",
    flag: (
      <>
        <span className="absolute left-0 top-0 w-4 h-[5.217px]" style={{ backgroundImage: "linear-gradient(90deg, #1F2129, #424658)" }} />
        <span className="absolute left-0 top-[5.22px] w-4 h-[5.217px]" style={{ backgroundImage: "linear-gradient(134.58deg, #D23E44 37.84%, #6C2023 114.09%)" }} />
        <span className="absolute left-0 top-[10.45px] w-4 h-[5.217px]" style={{ backgroundImage: "linear-gradient(204.29deg, #FFB909 0.8%, #E17200 94.83%)" }} />
      </>
    ),
    boxBorder: "linear-gradient(90deg, #FFB909, #D23E44 50%, #1F2129)",
    stackCount: true,
  },
  GB: {
    code: "gb",
    provider: "trustpilot",
    flag: (
      <svg className={svgFlagClass} viewBox="0 0 60 30" preserveAspectRatio="xMidYMid slice">
        <clipPath id="trust-badge-gb-flag"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" /></clipPath>
        <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
        <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#trust-badge-gb-flag)" stroke="#C8102E" strokeWidth="4" />
        <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
        <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
      </svg>
    ),
    boxBorder: "linear-gradient(90deg, #012169, #C8102E 50%, #1F2129)",
  },
  CH: {
    code: "ch",
    // Trusted Shops is blocked for CH (DISALLOWED_TRUSTEDSHOP_COUNTRIES in TrustedShopsBadge).
    provider: "trustpilot",
    flag: (
      <svg className={svgFlagClass} viewBox="0 0 32 32">
        <path d="M0 0h32v32H0z" fill="#D52B1E" />
        <path d="M13 6h6v7h7v6h-7v7h-6v-7H6v-6h7z" fill="#fff" />
      </svg>
    ),
    boxBorder: "linear-gradient(90deg, #D52B1E, #8C1C13 50%, #1F2129)",
    stackCount: true,
  },
  FR: {
    code: "fr",
    provider: "trustedshops",
    flag: (
      <svg className={svgFlagClass} viewBox="0 0 3 2" preserveAspectRatio="none">
        <path d="M0 0h1v2H0z" fill="#002395" />
        <path d="M1 0h1v2H1z" fill="#fff" />
        <path d="M2 0h1v2H2z" fill="#ED2939" />
      </svg>
    ),
    boxBorder: "linear-gradient(90deg, #002395, #ED2939 50%, #1F2129)",
    stackCount: true,
  },
};

// URLs use both "uk" and "gb" for the UK; anything unknown gets the German badge, like the translator's default.
export function getTrustBadgeLocale(country?: string): TrustBadgeLocale {
  const key = country?.toUpperCase() === "UK" ? "GB" : country?.toUpperCase() ?? "";
  return TRUST_BADGE_LOCALES[key] ?? TRUST_BADGE_LOCALES.DE;
}
